import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  FiTruck, 
  FiUser, 
  FiHash, 
  FiPhone, 
  FiMail, 
  FiMapPin, 
  FiCreditCard, 
  FiFileText, 
  FiSave, 
  FiArrowLeft,
  FiCheckCircle,
  FiActivity
} from "react-icons/fi";
import { 
  addSupplier, 
  updateSupplier, 
  getSupplierById 
} from "../services/supplierService";

const AddSupplierLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    gst_no: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    note: "",
    opening_balance: "",
    opening_balance_type: "Debit",
    credit_limit: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) loadSupplier();
  }, [id]);

  const loadSupplier = async () => {
    setLoading(true);
    try {
      const res = await getSupplierById(id);
      if (res?.status === "success" && res.data) {
        const data = res.data;
        setForm({
          name: data.name || "",
          gst_no: data.gst_no || "",
          contact_person: data.contact_person || "",
          phone: data.phone || "",
          email: data.email || "",
          address: data.address || "",
          note: data.note || "",
          opening_balance: String(data.opening_balance ?? ""),
          opening_balance_type: data.opening_balance_type || "Debit",
          credit_limit: String(data.credit_limit ?? ""),
        });
      } else {
        toast.error("Supplier node not identified in matrix");
      }
    } catch {
      toast.error("Intelligence Link Error while loading supplier");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: name === "gst_no" ? value.toUpperCase() : value,
    });
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    let temp = {};
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!form.name.trim()) temp.name = "Supplier identity required";
    if (!form.contact_person.trim()) temp.contact_person = "Liaison contact required";
    if (!form.gst_no.trim()) temp.gst_no = "GST identifier required";
    else if (!gstRegex.test(form.gst_no)) temp.gst_no = "Invalid GST format";
    if (!form.phone.trim()) temp.phone = "Communication endpoint required";
    else if (!/^\d{10}$/.test(form.phone)) temp.phone = "Endpoint must be 10 digits";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) temp.email = "Invalid transmission address";
    if (form.opening_balance === "") temp.opening_balance = "Initial credit/debit required";
    if (form.credit_limit === "") temp.credit_limit = "Tactical credit threshold required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Coordinate validation failed");
      return;
    }
    setLoading(true);
    try {
      const res = isEdit
        ? await updateSupplier(id, form)
        : await addSupplier(form);
      if (res.status === "success") {
        toast.success(res.message || "Supplier synchronized");
        navigate("/supplier-list");
      } else {
        toast.error(res.message || "Sync failed");
      }
    } catch {
      toast.error("Ecosystem Link Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 mb-5 px-3 px-lg-0">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <div className="card shadow-premium border-0 animate-fade-in-up overflow-hidden rounded-4">
            {/* HEADER */}
            <div className="card-header bg-primary py-4 px-4 border-0 d-flex justify-content-between align-items-center">
               <div className="d-flex align-items-center gap-3 text-white">
                  <div className="bg-white bg-opacity-20 p-2 rounded-3">
                     <FiTruck size={24} />
                  </div>
                  <div>
                     <h4 className="mb-0 fw-bold">{isEdit ? "Refine Supplier Node" : "Initialize Supplier Node"}</h4>
                     <p className="extra-small opacity-75 mb-0">Establishing logistical supply chain parameters</p>
                  </div>
               </div>
               <Link to="/supplier-list" className="btn btn-white-soft rounded-pill btn-sm px-3 fw-bold">
                  <FiArrowLeft className="me-2"/> Back to Registry
               </Link>
            </div>

            <div className="card-body p-4 p-md-5 bg-white">
              <form onSubmit={handleSubmit} className="premium-modern-form">
                <div className="row g-4">
                  
                  {/* IDENTITY SECTION */}
                  <div className="col-12 mb-2">
                     <h6 className="fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                        <span className="bg-primary-soft p-1 rounded-2"><FiUser size={14}/></span>
                        Supplier Identification
                     </h6>
                     <hr className="opacity-10" />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label text-muted small fw-bold">Full Supplier Identity *</label>
                    <input
                      name="name"
                      placeholder="Organization Name"
                      value={form.name}
                      onChange={handleChange}
                      className={`form-control border-0 bg-light-soft rounded-3 px-3 py-2 ${errors.name ? 'is-invalid' : ''}`}
                    />
                    {errors.name && <div className="invalid-feedback extra-small">{errors.name}</div>}
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label text-muted small fw-bold">Strategic Liaison *</label>
                    <input
                      name="contact_person"
                      placeholder="Contact Person Name"
                      value={form.contact_person}
                      onChange={handleChange}
                      className={`form-control border-0 bg-light-soft rounded-3 px-3 py-2 ${errors.contact_person ? 'is-invalid' : ''}`}
                    />
                    {errors.contact_person && <div className="invalid-feedback extra-small">{errors.contact_person}</div>}
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label text-muted small fw-bold">GST Identifier *</label>
                    <input
                      name="gst_no"
                      placeholder="GSTIN Format"
                      value={form.gst_no}
                      onChange={handleChange}
                      className={`form-control border-0 bg-light-soft rounded-3 px-3 py-2 font-monospace ${errors.gst_no ? 'is-invalid' : ''}`}
                    />
                    {errors.gst_no && <div className="invalid-feedback extra-small">{errors.gst_no}</div>}
                  </div>

                  {/* COMMUNICATION SECTION */}
                  <div className="col-12 mt-5 mb-2">
                     <h6 className="fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                        <span className="bg-primary-soft p-1 rounded-2"><FiPhone size={14}/></span>
                        Communication Endpoints
                     </h6>
                     <hr className="opacity-10" />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted small fw-bold">Phone Number *</label>
                    <div className="input-group">
                       <span className="input-group-text border-0 bg-light-soft text-muted"><FiPhone size={14}/></span>
                       <input
                        name="phone"
                        placeholder="10-digit number"
                        value={form.phone}
                        onChange={handleChange}
                        className={`form-control border-0 bg-light-soft rounded-end-3 px-3 py-2 ${errors.phone ? 'is-invalid' : ''}`}
                      />
                    </div>
                    {errors.phone && <div className="text-danger extra-small mt-1">{errors.phone}</div>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted small fw-bold">Transmission Email</label>
                    <div className="input-group">
                       <span className="input-group-text border-0 bg-light-soft text-muted"><FiMail size={14}/></span>
                       <input
                        name="email"
                        placeholder="supplier@example.com"
                        value={form.email}
                        onChange={handleChange}
                        className={`form-control border-0 bg-light-soft rounded-end-3 px-3 py-2 ${errors.email ? 'is-invalid' : ''}`}
                      />
                    </div>
                    {errors.email && <div className="text-danger extra-small mt-1">{errors.email}</div>}
                  </div>

                  {/* ECONOMY SECTION */}
                  <div className="col-12 mt-5 mb-2">
                     <h6 className="fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                        <span className="bg-primary-soft p-1 rounded-2"><FiCreditCard size={14}/></span>
                        Financial Parameters
                     </h6>
                     <hr className="opacity-10" />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-bold">Initialization Balance *</label>
                    <div className="input-group">
                       <span className="input-group-text border-0 bg-light-soft text-muted">₹</span>
                       <input
                        type="number"
                        name="opening_balance"
                        placeholder="0.00"
                        value={form.opening_balance}
                        onChange={handleChange}
                        className={`form-control border-0 bg-light-soft rounded-end-3 px-3 py-2 ${errors.opening_balance ? 'is-invalid' : ''}`}
                      />
                    </div>
                    {errors.opening_balance && <div className="text-danger extra-small mt-1">{errors.opening_balance}</div>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-bold">Balance Vector *</label>
                    <select
                      name="opening_balance_type"
                      value={form.opening_balance_type}
                      onChange={handleChange}
                      className="form-select border-0 bg-light-soft rounded-3 px-3 py-2"
                    >
                      <option value="Debit">Debit (+)</option>
                      <option value="Credit">Credit (-)</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-bold">Tactical Credit Threshold *</label>
                    <div className="input-group">
                       <span className="input-group-text border-0 bg-light-soft text-muted"><FiActivity size={14}/></span>
                       <input
                        type="number"
                        name="credit_limit"
                        placeholder="Threshold limit"
                        value={form.credit_limit}
                        onChange={handleChange}
                        className={`form-control border-0 bg-light-soft rounded-end-3 px-3 py-2 ${errors.credit_limit ? 'is-invalid' : ''}`}
                      />
                    </div>
                    {errors.credit_limit && <div className="text-danger extra-small mt-1">{errors.credit_limit}</div>}
                  </div>

                  {/* DESCRIPTIVE SECTION */}
                  <div className="col-12 mt-5 mb-2">
                     <h6 className="fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                        <span className="bg-primary-soft p-1 rounded-2"><FiMapPin size={14}/></span>
                        Geospatial & Technical Notes
                     </h6>
                     <hr className="opacity-10" />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted small fw-bold">Logistical Address</label>
                    <textarea
                      name="address"
                      rows="3"
                      placeholder="Matrix coordinates / physical location"
                      value={form.address}
                      onChange={handleChange}
                      className="form-control border-0 bg-light-soft rounded-3 px-3 py-2"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted small fw-bold">Technical Directives</label>
                    <textarea
                      name="note"
                      rows="3"
                      placeholder="Operational directives for this supplier node"
                      value={form.note}
                      onChange={handleChange}
                      className="form-control border-0 bg-light-soft rounded-3 px-3 py-2"
                    />
                  </div>

                  {/* SUBMIT ACTION */}
                  <div className="col-12 mt-5">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary-gradient w-100 py-3 rounded-pill shadow-sm fw-bold d-flex align-items-center justify-content-center gap-2"
                    >
                      {loading ? (
                        <div className="spinner-border spinner-border-sm" role="status"></div>
                      ) : (
                        <>
                          <FiSave /> {isEdit ? "Synchronize Supplier Node" : "Deploy Supplier to Registry"}
                        </>
                      )}
                    </button>
                    <div className="text-center mt-3">
                       <span className="extra-small text-muted d-flex align-items-center justify-content-center gap-2">
                          <FiCheckCircle className="text-success"/> Logistical compliance validated
                       </span>
                    </div>
                  </div>

                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSupplierLayer;
